import { useTranslation } from 'react-i18next';

import { notificationsActions, useAppDispatch } from '@modules';
import { NotificationTypesEnum } from '@modules/notifications/types';
import { EditUserParams } from '@modules/user/types';

export type UseNotifications = {
  showNotification: (...data: Parameters<typeof notificationsActions.add>) => void;
  [key: string]: (...args: any) => void;
};

const useNotifications = (): UseNotifications => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const showNotification: UseNotifications['showNotification'] = (...data) => {
    dispatch(notificationsActions.add(...data));
  };

  const globalNotification = (message: string, name: string) => {
    const data = { name, message, type: NotificationTypesEnum.error };
    showNotification(data);
  };

  const bindCardFailedNotification = () => {
    const data = { message: t('card.bind_failed'), type: NotificationTypesEnum.error };
    showNotification(data);
  };

  const bindCardErrorNotification = (title: string, message: string) => {
    const data = { title, message, type: NotificationTypesEnum.error };
    showNotification(data);
  };

  const bindCarsSuccessNotification = () => {
    const data = { message: t('card.bind_success') };
    showNotification(data);
  };

  const withdrawErrorNotification = (message: string) => {
    const data = { message, type: NotificationTypesEnum.error };
    showNotification(data);
  };

  const withdrawSuccessNotification = (message: string) => {
    const data = { title: t('notifications.thanks'), message };
    showNotification(data);
  };

  const strongPasswordNotification = () => {
    const data = {
      title: t('notify.strong_password.header'),
      message: t('notify.strong_password.on'),
    };
    showNotification(data);
  };

  const tonWarningNotification = (message: string) => {
    const data = { message };
    showNotification(data);
  };

  const successEditSettingsNotification = (
    field: keyof EditUserParams,
    value?: string | boolean
  ) => {
    const data = {
      title: t('public.notify_password_title'),
      message:
        field === 'strong_security'
          ? value
            ? t('notify.strong_password.on')
            : t('notify.strong_password.off')
          : t('settings.success_message'),
    };
    showNotification(data);
  };

  const setNewPasswordNotification = () => {
    const data = {
      title: t('public.notify_password_title'),
      message: t('public.notify_password_description'),
    };
    showNotification(data);
  };

  const rapydErrorLimitsNotification = () => {
    const data = {
      title: t('rapyd.errors.limits.title'),
      message: t('rapyd.errors.limits.subtitle'),
      type: NotificationTypesEnum.error,
    };
    showNotification(data);
  };

  const sellProcessedNotification = (message: string) => {
    const data = { message };
    showNotification(data);
  };

  const buyErrorNotification = () => {
    const data = {
      title: t('settings.error'),
      message: t('buy.order_failed'),
      type: NotificationTypesEnum.error,
    };
    showNotification(data);
  };

  const buyErrorNotificationBypass = error => {
    showNotification(error);
  };

  const buySuccessNotification = (message: string) => {
    const data = {
      title: t('notifications.thanks'),
      message: `${t('notifications.transaction_ordered')} ${message}`,
    };
    showNotification(data);
  };

  const showErrorPOANotification = () => {
    const data = {
      title: t('iban.status_failed_title'),
      message: t('iban.status_failed_text'),
    };
    showNotification(data);
  };

  const showSuccessPOANotification = () => {
    const data = {
      title: t('iban.status_success_title'),
      message: t('iban.status_success_text'),
    };
    showNotification(data);
  };

  const showErrorOpenAccount = () => {
    const data = {
      title: t('notifications.open_account_error_title'),
      message: t('notifications.open_account_error_message'),
    };
    showNotification(data);
  };

  const showSuccessOpenAccount = () => {
    const data = {
      title: t('notifications.open_account_success_title'),
      message: t('notifications.open_account_success_message'),
    };
    showNotification(data);
  };

  const showEqualPasswordsNotification = (message: string) => {
    const data = { message };
    showNotification(data);
  };

  return {
    showNotification,
    globalNotification,
    bindCardFailedNotification,
    bindCardErrorNotification,
    bindCarsSuccessNotification,
    withdrawErrorNotification,
    withdrawSuccessNotification,
    strongPasswordNotification,
    tonWarningNotification,
    successEditSettingsNotification,
    setNewPasswordNotification,
    rapydErrorLimitsNotification,
    sellProcessedNotification,
    buyErrorNotification,
    buyErrorNotificationBypass,
    buySuccessNotification,
    showErrorPOANotification,
    showSuccessPOANotification,
    showErrorOpenAccount,
    showSuccessOpenAccount,
    showEqualPasswordsNotification,
  };
};

export default useNotifications;
